import React, { memo, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Card, Divider, Stack, Typography } from 'investira.react.components';
import {
  AtivoConsistencia,
  AtivoIndicesRentabilidade,
  AtivoMetricasRisco,
  AtivoPatrimonio,
  AtivoRentabilidade,
  AtivosMetricasRisco
} from '../../../components/organisms';
import { buildComparadorFundosPrimaryLabel } from '../_shared/comparadorFundos.labels';
import { getComparadorFundosCommonBenchmark } from '../_shared/comparadorFundos.helpers';
import ComparadorFundosCorrelacao from './ComparadorFundosCorrelacao';
import ComparadorVisaoGeral from './ComparadorVisaoGeral';
import ComparadorAtivosInformacao from './ComparadorAtivosInformacao';
import ComparadorTaxasLiquidez from './ComparadorTaxasLiquidez';
import ComparadorGestorAdm from './ComparadorGestorAdm';
import { displays } from 'investira.react.lib';
import utils from '../../../utils';

const wComparisonSections = [
  { id: 'comparador-visao-geral', label: 'Visão geral' },
  { id: 'comparador-rentabilidade-historica', label: 'Rentabilidade Comparada' },
  { id: 'comparador-consistencia', label: 'Consistência' },
  { id: 'comparador-risco', label: 'Risco' },
  { id: 'comparador-correlacao', label: 'Correlação' },
  { id: 'comparador-patrimonio-liquido', label: 'Patrimônio líquido' },
  { id: 'comparador-custo-liquidez', label: 'Taxas' },
  { id: 'comparador-ativo-informacao', label: 'Informações dos Ativos' },
  { id: 'comparador-gestores-administradores', label: 'Gestores e Administradores' }
];

const wStickySpacing = 16;

/** Mantém explícita a ausência de um campo que não possui contrato nesta fase. */
function pvReadRealValue(pFundo, pFields) {
  const xValue = pFields
    .map(pField => pFundo?.[pField])
    .find(pValue => pValue !== null && pValue !== undefined && pValue !== '');

  return xValue === undefined ? 'Não disponível' : String(xValue);
}

/**
 * Localiza a superfície que realmente controla a rolagem vertical da página.
 *
 * @param {HTMLElement} pElement Elemento contido na superfície.
 * @returns {HTMLElement|null} Ancestral rolável ou null para a janela.
 */
function pvFindScrollableParent(pElement) {
  let xParent = pElement?.parentElement;

  while (xParent) {
    const xOverflowY = window.getComputedStyle(xParent).overflowY;
    const xCanScroll = ['auto', 'scroll'].includes(xOverflowY);

    if (xCanScroll && xParent.scrollHeight > xParent.clientHeight) {
      return xParent;
    }

    xParent = xParent.parentElement;
  }

  return null;
}

/**
 * Mantém as dez âncoras da comparação abertas e navegáveis sem antecipar os
 * dados analíticos da fase seguinte.
 */
const ComparadorFundosComparisonShell = memo(
  ({ comparisonFundos = [], cotacoesPorId = {}, highlightEnabled = false }) => {
    const [activeSectionId, setActiveSectionId] = useState(wComparisonSections[0].id);
    const indexRef = useRef(null);
    const xComparisonReady = comparisonFundos.length >= 2;
    const xAtivoIds = comparisonFundos.map(pFundo => String(pFundo.ifeed_ativo_id));
    const xPessoaIds = comparisonFundos.map(pFundo => pFundo.pessoa_id).filter(Boolean);
    const xDefaultIndicadorId = getComparadorFundosCommonBenchmark(comparisonFundos);

    useEffect(() => {
      if (!xComparisonReady || typeof IntersectionObserver === 'undefined') {
        return undefined;
      }

      const xStickyOffset = (indexRef.current?.offsetHeight || 0) + wStickySpacing;
      const xObserverRoot = pvFindScrollableParent(indexRef.current);
      const xObserver = new IntersectionObserver(
        pEntries => {
          const xVisibleEntry = pEntries.find(pEntry => pEntry.isIntersecting);

          if (xVisibleEntry) {
            setActiveSectionId(xVisibleEntry.target.id);
          }
        },
        {
          root: xObserverRoot,
          rootMargin: `-${xStickyOffset}px 0px -65% 0px`,
          threshold: 0
        }
      );

      wComparisonSections.forEach(pSection => {
        const xElement = document.getElementById(pSection.id);

        if (xElement) {
          xObserver.observe(xElement);
        }
      });

      return () => xObserver.disconnect();
    }, [xComparisonReady]);

    if (!xComparisonReady) {
      return null;
    }

    /** Navega para uma seção compensando a altura do índice sticky. */
    function pvNavigateToSection(pEvent, pSectionId) {
      pEvent.preventDefault();

      const xSection = document.getElementById(pSectionId);

      if (!xSection) {
        return;
      }

      const xStickyOffset = (indexRef.current?.offsetHeight || 0) + wStickySpacing;
      const xScrollParent = pvFindScrollableParent(xSection);

      setActiveSectionId(pSectionId);

      if (xScrollParent) {
        xScrollParent.scrollTo({
          top:
            xScrollParent.scrollTop +
            xSection.getBoundingClientRect().top -
            xScrollParent.getBoundingClientRect().top -
            xStickyOffset,
          behavior: 'smooth'
        });
        return;
      }

      window.scrollTo({
        top: xSection.getBoundingClientRect().top + window.scrollY - xStickyOffset,
        behavior: 'smooth'
      });
    }

    /** Renderiza somente dados observados; lacunas não recebem valores sintéticos. */
    function pvRenderSectionContent(pSectionId) {
      if (pSectionId === 'comparador-visao-geral') {
        return <ComparadorVisaoGeral ativos={comparisonFundos} />;
      }

      if (pSectionId === 'comparador-rentabilidade-historica') {
        return (
          <AtivoRentabilidade
            ativos={comparisonFundos}
            cotacoesPorId={cotacoesPorId}
            defaultIndicadorId={xDefaultIndicadorId}
            headerHighlightEnabled={false}
            indiceProps={{ subheaderEnabled: false, highlightEnabled: highlightEnabled }}
          />
        );
      }

      if (pSectionId === 'comparador-consistencia') {
        return (
          <AtivoConsistencia
            ativos={comparisonFundos}
            cotacoesPorId={cotacoesPorId}
            highlightEnabled={highlightEnabled}
          />
        );
      }

      if (pSectionId === 'comparador-risco') {
        return (
          <AtivoMetricasRisco
            ativos={comparisonFundos}
            highlightEnabled={highlightEnabled}
            subheaderEnabled={false}
          />
        );
      }

      if (pSectionId === 'comparador-patrimonio-liquido') {
        return xPessoaIds.length ? (
          <AtivoPatrimonio pessoaIds={xPessoaIds} dataHoje={utils.dates.lastMonthDayValid()} />
        ) : (
          <Alert severity="info">Não disponível: o detalhe não informou a pessoa emissora.</Alert>
        );
      }

      if (pSectionId === 'comparador-correlacao') {
        return <ComparadorFundosCorrelacao fundos={comparisonFundos} />;
      }

      if (pSectionId === 'comparador-ativo-informacao') {
        return <ComparadorAtivosInformacao ativos={comparisonFundos} />;
      }

      if (pSectionId === 'comparador-custo-liquidez') {
        return (
          <ComparadorTaxasLiquidez ativos={comparisonFundos} highlightEnabled={highlightEnabled} />
        );
      }

      if (pSectionId === 'comparador-gestores-administradores') {
        return <ComparadorGestorAdm ativos={comparisonFundos} />;
      }

      return null;
    }

    return (
      <Stack
        id="comparador-comparison-shell"
        spacing={3}
        sx={{ width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'clip' }}>
        <Box
          ref={indexRef}
          component="nav"
          aria-label="Índice da comparação"
          data-comparador-index-scroll="horizontal"
          sx={{
            position: 'sticky',
            top: '-24px',
            zIndex: 10,
            width: '100%',
            maxWidth: '100%',
            overflowX: 'auto',
            backgroundColor: 'background.dark'
          }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ width: 'max-content', minWidth: '100%', py: 1, px: 1 }}>
            {wComparisonSections.map(pSection => {
              const xIsActive = activeSectionId === pSection.id;

              return (
                <Button
                  key={pSection.id}
                  component="a"
                  href={`#${pSection.id}`}
                  aria-current={xIsActive ? 'location' : undefined}
                  onClick={pEvent => pvNavigateToSection(pEvent, pSection.id)}
                  variant={xIsActive ? 'contained' : 'text'}
                  size="small"
                  sx={{
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: 2
                    }
                  }}>
                  {pSection.label}
                </Button>
              );
            })}
          </Stack>
          <Divider />
        </Box>

        {wComparisonSections.map(pSection => (
          <Stack
            key={pSection.id}
            id={pSection.id}
            component="section"
            sx={{
              scrollMarginTop: `${wStickySpacing}px`,
              width: '100%',
              maxWidth: '100%',
              minWidth: 0
            }}>
            {pvRenderSectionContent(pSection.id)}
          </Stack>
        ))}
      </Stack>
    );
  }
);

export default ComparadorFundosComparisonShell;
